import { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import { buildIdCardData, idCardImageCacheKey } from '../utils/idCardCardData';
import { buildFrontHtml, buildBackHtml } from '../utils/idCardHtmlBuilder';
import { ID_CARD_PORTRAIT, ID_CARD_LANDSCAPE } from '../utils/idCardTheme';

export const CARD_FORMATS = {
  portrait: ID_CARD_PORTRAIT,
  landscape: ID_CARD_LANDSCAPE,
};

function preloadImage(url) {
  if (!url) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * Shared ID card template state — single source for preview HTML.
 */
export function useIdCardTemplate({
  student,
  school,
  photoUrl,
  imageCacheKey = '',
  assetOverrides = null,
  previewVersion = 0,
  format = 'portrait',
  design = 'saffron',
}) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [assetsReady, setAssetsReady] = useState(true);

  const cacheKey = useMemo(
    () => idCardImageCacheKey(school, imageCacheKey),
    [school, imageCacheKey],
  );

  const base = useMemo(
    () => buildIdCardData(student, school, photoUrl, cacheKey, assetOverrides),
    [student, school, photoUrl, cacheKey, assetOverrides],
  );

  useEffect(() => {
    let cancelled = false;
    if (!base.qrPayload) {
      setQrDataUrl('');
      return undefined;
    }
    QRCode.toDataURL(base.qrPayload, { width: 480, margin: 1, errorCorrectionLevel: 'M' })
      .then((u) => { if (!cancelled) setQrDataUrl(u); })
      .catch(() => { if (!cancelled) setQrDataUrl(''); });
    return () => { cancelled = true; };
  }, [base.qrPayload]);

  const data = useMemo(() => ({ ...base, qrDataUrl }), [base, qrDataUrl]);
  const size = CARD_FORMATS[format] || CARD_FORMATS.portrait;
  const portrait = format !== 'landscape';

  useEffect(() => {
    let cancelled = false;
    setAssetsReady(false);
    const urls = [
      data.frontBgUrl,
      data.backBgUrl,
      data.footerBgUrl,
      data.principalSignatureUrl,
      data.logoUrl,
      data.photoUrl,
      data.managedLogoUrl,
    ].filter(Boolean);
    if (!urls.length) {
      setAssetsReady(true);
      return undefined;
    }
    Promise.all(urls.map((u) => preloadImage(u))).then(() => {
      if (!cancelled) setAssetsReady(true);
    });
    return () => { cancelled = true; };
  }, [
    data.frontBgUrl,
    data.backBgUrl,
    data.footerBgUrl,
    data.principalSignatureUrl,
    data.logoUrl,
    data.photoUrl,
    data.managedLogoUrl,
    previewVersion,
  ]);

  const frontHtml = useMemo(
    () => buildFrontHtml(data, size.widthPx, size.heightPx, portrait, design),
    [data, size.widthPx, size.heightPx, portrait, design],
  );
  const backHtml = useMemo(
    () => buildBackHtml(data, size.widthPx, size.heightPx, portrait, design),
    [data, size.widthPx, size.heightPx, portrait, design],
  );

  const previewKey = `${cacheKey}-${previewVersion}-${format}-${design}-${data.logoUrl}-${data.footerBgUrl}-${data.principalSignatureUrl}-${data.managedLogoUrl}`;

  return {
    frontHtml,
    backHtml,
    data,
    size,
    portrait,
    design,
    assetsReady,
    cacheKey,
    previewKey,
  };
}
